import { NgClass } from '@angular/common';
import {
    Component,
    input,
    signal,
    inject,
    resource,
    linkedSignal,
    effect,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApiService } from '../../../services/api.service';
import { CartService } from '../../../services/cart.service';
import { DOMHandlerService } from '../../../services/domhandler.service';
import { ButtonComponent } from '../../common/button/button.component';
import { RichTextComponent } from '../../common/rich-text/rich-text.component';
import { Image } from '../../apartment/header/header.component';
import { firstValueFrom } from 'rxjs';
import { DeliveryDate, Product, Variant } from '../product/product.component';
import {
    TranslateDirective,
    TranslateModule,
    TranslateParser,
    TranslatePipe,
    TranslateService,
} from '@ngx-translate/core';
import { CheckoutService } from '../../../services/checkout.service';
import { DividerComponent } from '../../common/divider/divider.component';
import { BlockNode } from '../../../types/rich-text.types';
import { LanguageService } from '../../../services/language.service';
import { CustomPricePipe } from '../../../pipes/price.pipe';
import { CustomDatePipe } from '../../../pipes/date.pipe';
import { ToastrService } from 'ngx-toastr';
import { SharedNgxskeletonloaderModule } from '../../../services/ngxskeletonloader.module';
import { ProductService } from '../../../services/product.service';
import { ShopService } from '../../../services/shop.service';
import { Title } from '@angular/platform-browser';
import { UmamiService } from '../../../services/umami.service';

@Component({
    selector: 'app-full-product',
    templateUrl: './full-product.component.html',
    host: { class: 'container mx-auto block' },
    standalone: true,
    imports: [
        FormsModule,
        ButtonComponent,
        RichTextComponent,
        DividerComponent,
        NgClass,
        ButtonComponent,
        TranslatePipe,
        TranslateModule,
        TranslateDirective,
        CustomPricePipe,
        CustomDatePipe,
        SharedNgxskeletonloaderModule,
        RouterLink,
    ],
})
export class FullProductComponent {
    data = input.required<FullProduct>();
    apiService = inject(ApiService);
    checkoutService = inject(CheckoutService);
    languageService = inject(LanguageService);
    productService = inject(ProductService);
    umami = inject(UmamiService);
    productSlug = signal('');
    productResource = resource({
        request: () => ({
            slug: this.productSlug(),
        }),
        loader: ({ request }) =>
            firstValueFrom(this.apiService.getProductBySlug(request.slug)),
    });
    selectedImage = linkedSignal(() => {
        return this.productResource.value()?.data[0]?.images?.[0] ?? undefined;
    });
    selectedVariant = linkedSignal(() => {
        return this.getFirstAvailableVariant(
            this.productResource.value()!.data[0],
        );
    });
    selectedDeliveryDate = linkedSignal(() => {
        return (
            this.productResource.value()?.data[0]?.deliveryDates?.[0] ??
            undefined
        );
    });
    price = linkedSignal(() => {
        if (
            this.productResource.value() &&
            this.productResource.value()!.data.length > 0 &&
            this.selectedVariant()
        ) {
            return this.cartService.calculatePrice({
                product: this.productResource.value()!.data[0],
                selectedVariant: this.selectedVariant()!,
                amount: this.amount(),
            });
        }
        return 0;
    });
    amount = signal<number>(1);
    cartService: CartService = inject(CartService);
    domHandler: DOMHandlerService = inject(DOMHandlerService);
    showAddToCartAnimation = false;
    toastService = inject(ToastrService);
    translationService = inject(TranslateService);
    translationParser = inject(TranslateParser);
    shopService = inject(ShopService);
    isRevalidating = signal(false);
    titleService = inject(Title);
    private activatedRoute = inject(ActivatedRoute);
    private router = inject(Router);

    constructor() {
        effect(() => {
            if (this.productResource.value()) {
                this.titleService.setTitle(
                    `Zehentmoar - ${this.productResource.value()!.data[0].name}`,
                );
                if (this.productResource.value()!.data.length > 0) {
                    this.activatedRoute.queryParams.subscribe((params) => {
                        if (
                            params['category'] !==
                            this.productResource.value()?.data[0].category.slug
                        ) {
                            this.router.navigate([], {});
                        }
                    });
                    this.domHandler.addStructuredData(
                        this.productService.getJsonLd(
                            this.productResource.value()!.data[0],
                        ),
                    );
                }
            }
        });
    }

    ngOnInit() {
        this.activatedRoute.params.subscribe((params) => {
            this.productSlug.set(params['productSlug']);
        });
    }

    getCategoryName() {
        return this.translationService.instant('product.allProducts', {
            category: this.productResource.value()!.data[0].category.name,
        });
    }

    revalidateItem() {
        this.isRevalidating.set(true);
        const cartItem = {
            product: this.productResource.value()!.data[0],
            selectedVariant: this.selectedVariant()!,
            amount: this.amount(),
            selectedDeliveryDate: this.selectedDeliveryDate(),
        };
        this.translationService
            .get('product.notAvailableAnymore')
            .subscribe((translation) => {
                this.checkoutService
                    .revalidateCartItem(cartItem, translation)
                    .subscribe((res) => {
                        if (res) {
                            this.checkoutService.redirectToCheckoutForOneProduct(
                                cartItem,
                            );
                        }
                        this.isRevalidating.set(false);
                    });
            });
    }

    getFirstAvailableVariant(product: Product) {
        return product.variants.find((variant) => variant.amount > 0);
    }

    selectImage(image: Image) {
        this.selectedImage.set(image);
    }

    selectVariant(variant: Variant) {
        this.selectedVariant.set(variant);
        this.amount.set(Math.min(variant.amount, this.amount()));
        this.calculatePrice();
        this.cartService.umami.track('change-variant', {
            variant: this.productService.getVariantLabel(
                this.productResource.value()!.data[0],
                variant,
            ),
            product: this.productResource.value()!.data[0].name,
        });
    }

    selectDeliveryDate(deliveryDate: DeliveryDate) {
        this.selectedDeliveryDate.set(deliveryDate);
        this.cartService.umami.track('change-delivery-date', {
            date: deliveryDate.date,
            product: this.productResource.value()!.data[0].name,
        });
    }

    setAmount(event: Event) {
        if (event.target) {
            const value = parseInt((event.target as HTMLInputElement).value);
            if (value > 0 && value <= 50) {
                this.amount.set(value);
                this.calculatePrice();
            }
            if (value < 0) {
                this.amount.set(1);
                this.calculatePrice();
            }
            if (value > 50) {
                this.amount.set(50);
                this.calculatePrice();
            }
        }
    }

    calculatePrice() {
        if (!this.selectedVariant()) {
            return;
        }
        this.price.set(
            this.cartService.calculatePrice({
                product: this.productResource.value()!.data[0],
                selectedVariant: this.selectedVariant()!,
                amount: this.amount(),
            }),
        );
    }

    increaseQuantity() {
        if (this.amount() >= 50) {
            this.translationService
                .get('product.maxAmount')
                .subscribe((res) => {
                    this.toastService.error(res);
                });
            return;
        }

        if (this.amount() >= (this.selectedVariant()?.amount || 50)) {
            this.translationService
                .get('product.onlyXLeft')
                .subscribe((res) => {
                    this.toastService.error(
                        this.translationParser.interpolate(res, {
                            amount: this.selectedVariant()?.amount || 50,
                        }),
                    );
                });
            return;
        }

        this.amount.set(this.amount() + 1);
        this.calculatePrice();
        this.cartService.umami.track('increase-quantity', {
            product: this.productResource.value()!.data[0].name,
        });
    }

    decreaseQuantity() {
        if (this.amount() <= 1) {
            this.translationService.get('product.minOne').subscribe((res) => {
                this.toastService.error(res);
            });
            return;
        }

        if (this.amount() > 1) {
            this.amount.set(this.amount() - 1);
        }
        this.calculatePrice();
        this.cartService.umami.track('decrease-quantity', {
            product: this.productResource.value()!.data[0].name,
        });
    }
}

type FullProduct = {
    infoText: BlockNode[];
};
